<!--
 * Path: packages/frontend/src/core/components/app/AppGlobalSearch.vue
 * Task: Global search overlay accessible via Menubar or keyboard shortcut.
 * Refactoring: Added dynamic density and size props to adapt styling for compact modes.
-->
<template>
  <div class="d-flex align-center">
    <!-- Trigger Button -->
    <v-btn
      icon
      variant="text"
      :density="density"
      :size="size"
      color="medium-emphasis"
      class="mx-1 hover-btn transition-swing"
      @click="isOpen = true"
    >
      <v-icon size="20">mdi-magnify</v-icon>
      <v-tooltip activator="parent" location="bottom">Search (Ctrl+K)</v-tooltip>
    </v-btn>

    <!-- Search Dialog -->
    <v-dialog v-model="isOpen" max-width="600" transition="dialog-top-transition" @keydown.esc="isOpen = false">
      <v-card class="rounded-xl overflow-hidden elevation-24">
        <!-- Search Input Header -->
        <v-toolbar color="surface" density="comfortable" class="border-b px-2">
          <v-icon color="primary" class="me-3 ms-2">mdi-magnify</v-icon>
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            class="global-search-input flex-grow-1"
            placeholder="Search all entities..."
            autocomplete="off"
            @keydown.enter="selectFirstResult"
            @keydown.down.prevent="navigateResults(1)"
            @keydown.up.prevent="navigateResults(-1)"
          />
          <v-btn icon="mdi-close" variant="text" size="small" color="medium-emphasis" @click="isOpen = false"></v-btn>
        </v-toolbar>

        <!-- Search Results List -->
        <v-list v-if="searchResults.length > 0" class="pa-2 bg-surface" max-height="400" style="overflow-y: auto;">
          <v-list-item
            v-for="(entity, index) in searchResults"
            :key="entity.id"
            :class="{'bg-primary-lighten-5 text-primary': activeIndex === index}"
            class="rounded-lg mb-1 cursor-pointer transition-swing"
            @click="openEntity(entity)"
            @mouseover="activeIndex = index"
          >
            <template v-slot:prepend>
              <EntityIcon :entity="entity" size="28" class="me-3" />
            </template>
            <v-list-item-title class="font-weight-bold text-body-1">{{ entity.title || entity.id }}</v-list-item-title>
            <v-list-item-subtitle class="text-caption text-truncate">{{ entity.description || 'No description available.' }}</v-list-item-subtitle>

            <template v-slot:append>
              <v-chip size="x-small" variant="tonal" class="text-uppercase font-weight-bold">
                {{ entity.entityType }}
              </v-chip>
            </template>
          </v-list-item>
        </v-list>

        <!-- Empty State -->
        <div v-else-if="searchQuery.trim().length > 0" class="pa-8 text-center text-medium-emphasis">
          <v-icon size="48" class="mb-3 opacity-50">mdi-text-box-search-outline</v-icon>
          <div class="text-body-1">No results found for "{{ searchQuery }}"</div>
        </div>

        <!-- Initial Helper State -->
        <div v-else class="pa-6 text-center text-medium-emphasis bg-grey-lighten-4">
          <div class="text-caption">Type to instantly search across all titles, descriptions, and IDs in the active project.</div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { useDatastoreStore } from '@/store/datastore';
import { useWorkspaceStore } from '@/store/workspace';
import { useFilterStore } from '@/store/filters';
import { useUiStore } from '@/store/ui';
import type { Entity } from '@/core/types/editor';
import EntityIcon from '@/core/components/common/EntityIcon.vue';

const props = defineProps({
  density: {
    type: String,
    default: 'default'
  },
  size: {
    type: String,
    default: 'default'
  }
});

const isOpen = ref(false);
const searchQuery = ref('');
const searchInputRef = ref<HTMLInputElement | null>(null);
const activeIndex = ref(0);

const datastore = useDatastoreStore();
const workspaceStore = useWorkspaceStore();
const filterStore = useFilterStore();
const uiStore = useUiStore();

// Focus input automatically when dialog opens
watch(isOpen, (val) => {
  if (val) {
    searchQuery.value = '';
    activeIndex.value = 0;
    nextTick(() => {
      searchInputRef.value?.focus();
    });
  }
});

// Calculate search results based on the entire datastore
const searchResults = computed<Entity[]>(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query || !datastore.allEntities) return [];

  return datastore.allEntities.filter((entity: Entity) => {
    return (entity.title?.toLowerCase() || '').includes(query) ||
      (entity.description?.toLowerCase() || '').includes(query) ||
      (entity.id.toLowerCase()).includes(query);
  }).slice(0, 20); // Limit to top 20 for performance
});

// Reset active index when query changes
watch(searchQuery, () => {
  activeIndex.value = 0;
});

// Keyboard Navigation
const navigateResults = (direction: number) => {
  const max = searchResults.value.length - 1;
  let next = activeIndex.value + direction;
  if (next < 0) next = max;
  if (next > max) next = 0;
  activeIndex.value = next;
};

const selectFirstResult = () => {
  if (searchResults.value.length > 0) {
    openEntity(searchResults.value[activeIndex.value]);
  }
};

// Navigation logic to open the entity in the editor
const openEntity = (entity: Entity) => {
  isOpen.value = false;

  // Set up the context in the Editor
  filterStore.filterQuery = [{ id: 'root', types: [entity.entityType], instances: [] }];
  filterStore.statusFilter = [];
  filterStore.textFilter = '';
  filterStore.dynamicFilters = {};

  workspaceStore.setSelectedEntity(entity.id);
  uiStore.setActiveView('editor', true);
};

// Global Keyboard Shortcut Listener (Ctrl+K or Cmd+K)
const handleGlobalKeydown = (e: KeyboardEvent) => {
  if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
    e.preventDefault();
    isOpen.value = true;
  }
};

onMounted(() => {
  window.addEventListener('keydown', handleGlobalKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleGlobalKeydown);
});
</script>

<style scoped>
.global-search-input {
  border: none;
  outline: none;
  font-size: 1.1rem;
  background: transparent;
  color: rgb(var(--v-theme-on-surface));
  padding: 8px;
}
.global-search-input::placeholder {
  color: rgba(var(--v-theme-on-surface), 0.5);
}
.hover-btn:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.04);
}
.transition-swing {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}
</style>
